<template>
  <b-modal
    :visible="visible"
    size="lg"
    hide-header-close
    no-close-on-backdrop
    no-close-on-esc
    no-enforce-focus
    no-stacking
    centered
    :title="project ? project.name : ''"
    @hide="onHide"
    @hidden="onHidden"
  >
    <div v-if="isLoading" class="text-center py-4">
      <b-spinner variant="primary" label="Loading..."></b-spinner>
      <p class="mt-2 text-muted">Loading project...</p>
    </div>

    <b-alert v-else-if="error && !project" show variant="danger">
      {{ error }}
    </b-alert>

    <div v-else-if="project">
      <div class="text-muted mb-3">
        <small>
          <span>Created: {{ formatDate(project.createdAt) }}</span>
          <span class="ml-3">Updated: {{ formatDate(project.updatedAt) }}</span>
        </small>
      </div>

      <b-alert v-if="error" show variant="danger" dismissible @dismissed="error = null">
        {{ error }}
      </b-alert>
      <b-alert v-if="success" show variant="success" dismissible @dismissed="success = null">
        {{ success }}
      </b-alert>

      <b-form @submit.prevent="handleSubmit">
        <b-form-group class="mb-4">
          <b-form-checkbox
            v-model="formData.isFeatured"
            :disabled="isSaving"
            size="lg"
          >
            <strong>Featured Project</strong>
            <br>
            <small class="text-muted">
              Featured projects are highlighted on the homepage
            </small>
          </b-form-checkbox>
        </b-form-group>

        <b-form-group class="mb-4">
          <div class="d-flex align-items-center">
            <b-form-checkbox
              v-model="formData.isSciStarter"
              :value="true"
              :unchecked-value="false"
              :disabled="isSaving"
              switch
              size="lg"
            >
              <strong>SciStarter Sync</strong>
            </b-form-checkbox>
          </div>
          <small class="text-muted">
            {{ formData.isSciStarter ? 'Project is synced with SciStarter' : 'Project is not synced with SciStarter' }}
          </small>
        </b-form-group>

        <b-form-group class="mb-4">
          <div class="d-flex align-items-center">
            <b-form-checkbox
              v-model="formData.isRichText"
              :value="true"
              :unchecked-value="false"
              :disabled="isSaving"
              switch
              size="lg"
            >
              <strong>Rich Text for Tasks</strong>
            </b-form-checkbox>
          </div>
          <small class="text-muted">
            {{ formData.isRichText ? 'Rich Text for Tasks is turned On' : 'Rich Text for Tasks is turned Off' }}
          </small>
        </b-form-group>

        <b-form-group label="Project Status" label-class="font-weight-bold" class="mb-4">
          <b-form-select
            v-model="formData.projectState"
            :options="projectStateOptions"
            :disabled="isSaving"
          ></b-form-select>
          <small class="text-muted">
            Draft projects are only visible to their managers
          </small>
        </b-form-group>
      </b-form>
    </div>

    <template #modal-footer>
      <b-button variant="secondary" @click="onHide" :disabled="isSaving">
        Cancel
      </b-button>
      <b-button
        variant="primary"
        @click="handleSubmit"
        :disabled="isSaving || !project"
      >
        <b-spinner small v-if="isSaving" class="mr-1"></b-spinner>
        {{ isSaving ? 'Saving...' : 'Save Changes' }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

export default {
  name: "AdminProjectEditModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    projectId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      error: null,
      success: null,
      formData: {
        isFeatured: false,
        isSciStarter: true,
        projectState: 1,
      },
      projectStateOptions: [
        { value: 0, text: "Draft" },
        { value: 1, text: "Published" },
      ],
    };
  },
  computed: {
    ...mapGetters("admin/projects", {
      project: "selectedProject",
      isLoading: "isLoading",
      isSaving: "isSaving",
      saveError: "saveError",
    }),
  },
  methods: {
    ...mapActions("admin/projects", [
      "getProject",
      "updateProject",
      "clearSelectedProject",
      "clearSaveError",
    ]),
    ...mapActions("admin/stats", {
      refreshStats: "getStats",
    }),

    async loadProject() {
      if (!this.projectId) return;

      this.error = null;
      this.success = null;

      try {
        const data = await this.getProject(this.projectId);
        this.formData = {
          isFeatured: data.isFeatured,
          isSciStarter: data.isSciStarter,
          projectState: data.projectState,
          isRichText: data.isRichText,
        };
      } catch (err) {
        console.error("Error loading project:", err);
        this.error = "Failed to load project. Please try again.";
      }
    },

    async handleSubmit() {
      if (!this.project) return;

      this.error = null;
      this.success = null;

      try {
        await this.updateProject({
          id: this.projectId,
          data: this.formData,
        });

        // Force refresh stats in case featured/status changed
        this.refreshStats(true);

        // Emit saved event and close modal
        this.$emit("saved", this.project);
        this.onHide();
      } catch (err) {
        console.error("Error saving project:", err);
        this.error = "Failed to update project. Please try again.";
      }
    },

    onHide() {
      this.$emit("update:visible", false);
      this.$emit("close");
    },

    onHidden() {
      this.clearSelectedProject();
      this.error = null;
      this.success = null;
      this.formData = {
        isFeatured: false,
        isSciStarter: true,
        projectState: 1,
      };
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      return new Date(dateString).toLocaleDateString();
    },
  },
  watch: {
    visible(newVal) {
      if (newVal && this.projectId) {
        this.loadProject();
      }
    },
    projectId(newVal) {
      if (this.visible && newVal) {
        this.loadProject();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
</style>
